import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Asset, AssetType, AssetStatus, Campaign } from '../types.ts';
import { 
  X, CheckCircle, Clock, Lock, Archive, Edit2, 
  ChevronLeft, ChevronRight, Download, Trash2, 
  Loader2
} from 'lucide-react';
import { storage } from '../lib/firebase.ts';
import { ref, getBlob } from 'firebase/storage';

// --- CONFIG ---
export const STATUS_CONFIG = {
  [AssetStatus.Available]: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle, label: 'Available' },
  [AssetStatus.Reserved]: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Clock, label: 'Reserved' },
  [AssetStatus.Deployed]: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: Lock, label: 'Deployed' },
  [AssetStatus.Archived]: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400', icon: Archive, label: 'Archived' },
  [AssetStatus.Draft]: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400', icon: Edit2, label: 'Draft' },
  [AssetStatus.Approved]: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle, label: 'Approved' },
};

export const formatDate = (ts: number) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(ts));
};

export const DeleteConfirmationModal: React.FC<{
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing: boolean;
}> = ({ isOpen, title, message, onConfirm, onCancel, isProcessing }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-md border border-[var(--border-subtle)] animate-slideUp overflow-hidden transform transition-all">
        <div className="p-6 text-center">
          <div className="w-12 h-12 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 size={24} />
          </div>
          <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2">{title}</h3>
          <div className="text-[var(--text-secondary)] text-sm mb-6">{message}</div>
          
          <div className="flex gap-3 justify-center">
            <button
              onClick={onCancel}
              disabled={isProcessing}
              className="px-4 py-2 bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-lg font-medium hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isProcessing}
              className="px-6 py-2 bg-red-50 text-white rounded-lg font-medium hover:bg-red-600 transition-colors flex items-center gap-2 shadow-lg shadow-red-500/20"
            >
              {isProcessing ? <Loader2 className="animate-spin" size={16} /> : null}
              {isProcessing ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const AssetModal: React.FC<{ 
  asset: Asset; 
  onClose: () => void; 
  onUpdate: (id: string, data: Partial<Asset>) => Promise<void>; 
  onRequestDelete: (id: string) => void; 
  campaigns: Campaign[]; 
  creatorInfo: { name: string, avatar?: string };
}> = ({ asset, onClose, onUpdate, onRequestDelete, campaigns, creatorInfo }) => {
  
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEditName, setIsEditName] = useState(false);
  const [newName, setNewName] = useState('');

  const statusConfig = STATUS_CONFIG[asset.status] || { icon: CheckCircle, label: asset.status, color: 'bg-gray-100' };
  const StatusIcon = statusConfig.icon;

  // Build hierarchical options for campaign selection
  const campaignOptions = useMemo(() => {
    const getChildren = (parentId: string | undefined, depth: number): { c: Campaign, depth: number }[] => {
      // Find direct children
      const children = campaigns
        .filter(c => c.parentId === parentId || (!parentId && !c.parentId))
        .sort((a, b) => a.name.localeCompare(b.name));
      
      return children.flatMap(child => [
        { c: child, depth }, 
        ...getChildren(child.id, depth + 1)
      ]);
    };
    
    // Start with roots (undefined or null parentId)
    return getChildren(undefined, 0);
  }, [campaigns]);

  const handleUpdateStatus = async (status: AssetStatus) => {
      if (isUpdating || asset.status === status) return;
      setIsUpdating(true);
      try {
          await onUpdate(asset.id, { status });
      } finally {
          setIsUpdating(false);
      }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onRequestDelete(asset.id);
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);

    // Safety timeout to prevent infinite spinning
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error("Download Request Timed Out")), 30000)
    );

    const downloadTask = async () => {
      try {
        let blob: Blob | null = null;
        let filename = `chip_${(asset.scenario || 'asset').replace(/[^a-z0-9]/gi, '_').toLowerCase().substring(0, 30)}_${Date.now()}`;

        // STRATEGY 1: Data URI (Direct synchronous conversion)
        if (asset.url.startsWith('data:')) {
           const parts = asset.url.split(',');
           const mime = parts[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
           const bstr = atob(parts[1]);
           let n = bstr.length;
           const u8arr = new Uint8Array(n);
           while (n--) {
              u8arr[n] = bstr.charCodeAt(n);
           }
           blob = new Blob([u8arr], { type: mime });
        } 
        
        // STRATEGY 2: Firebase Storage SDK (Authenticated - Bypasses Public CORS)
        else if (storage && asset.url.includes('firebasestorage')) {
           try {
             const httpsRef = ref(storage, asset.url);
             // Download via SDK which uses internal auth
             blob = await getBlob(httpsRef);
           } catch (sdkErr) {
             console.warn("SDK Download failed, attempting fetch fallback...", sdkErr);
           }
        }

        // STRATEGY 3: Standard Fetch (Fallback)
        if (!blob) {
           const response = await fetch(asset.url, { 
             mode: 'cors',
             cache: 'no-cache'
           });
           if (!response.ok) throw new Error("Network response was not ok");
           blob = await response.blob();
        }

        if (!blob) throw new Error("All download strategies failed to produce a blob.");

        // Determine Extension
        const mimeType = blob.type;
        if (mimeType.includes('image/png')) filename += '.png';
        else if (mimeType.includes('image/jpeg')) filename += '.jpg';
        else if (mimeType.includes('video/mp4')) filename += '.mp4';
        else if (mimeType.includes('text')) filename += '.txt';
        else if (asset.type === AssetType.Image) filename += '.jpg'; // Fallback
        else if (asset.type === AssetType.Video) filename += '.mp4';

        // Trigger Download
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 2000);

      } catch (e) {
        console.error("Download Task Error:", e);
        // Fallback: Open in new tab if programmatic download fails
        window.open(asset.url, '_blank');
      }
    };

    try {
      await Promise.race([downloadTask(), timeoutPromise]);
    } catch (error) {
      console.error("Download failed or timed out:", error);
      // Final Fallback: New Tab
      window.open(asset.url, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-[var(--bg-primary)] w-full max-w-6xl h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row animate-slideUp border border-[var(--border-subtle)] relative">
        
        {/* Close Button */}
        <button 
           onClick={onClose} 
           className="absolute top-4 right-4 z-[60] p-2 bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-full hover:bg-[var(--bg-tertiary)] transition-colors shadow-sm border border-[var(--border-subtle)]"
         >
           <X size={20} />
        </button>

        {/* LEFT: Preview Area */}
        <div className="w-full md:w-2/3 bg-[var(--bg-secondary)] flex flex-col relative border-r border-[var(--border-subtle)]">
           <div className="absolute top-4 left-4 z-10 flex gap-2">
              <div className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-sm ${statusConfig.color}`}>
                 <StatusIcon size={14} /> {statusConfig.label}
              </div>
           </div>
           
           <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
              {asset.type === AssetType.Image && (
                <img src={asset.url} alt="Preview" className="max-w-full max-h-full object-contain shadow-2xl rounded-lg" />
              )}
              {asset.type === AssetType.Video && (
                 <video controls autoPlay className="max-w-full max-h-full rounded-lg shadow-2xl">
                   <source src={asset.url} type="video/mp4" />
                 </video>
              )}
              {asset.type === AssetType.Caption && (
                 <div className="w-full max-w-2xl bg-[var(--bg-primary)] p-8 rounded-xl shadow-xl border border-[var(--border-subtle)] text-lg leading-relaxed whitespace-pre-wrap font-serif">
                    {asset.url}
                 </div>
              )}
           </div>

           {/* Navigation Mockup */}
           <div className="h-16 border-t border-[var(--border-subtle)] bg-[var(--bg-primary)] flex items-center justify-between px-6">
               <button className="flex items-center gap-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50">
                 <ChevronLeft size={16} /> Previous Asset
               </button>
               <button className="flex items-center gap-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50">
                 Next Asset <ChevronRight size={16} />
               </button>
           </div>
        </div>

        {/* RIGHT: Details Area */}
        <div className="w-full md:w-1/3 bg-[var(--bg-primary)] flex flex-col h-full overflow-hidden">
           <div className="flex-1 overflow-y-auto p-6 space-y-8 pt-20"> 
              {/* Header Info */}
              <div>
                 <div className="flex items-start justify-between gap-2 mb-2">
                   {isEditName ? (
                     <div className="flex-1 flex gap-2">
                       <input 
                         autoFocus
                         className="input-field py-1 px-2 text-lg font-bold"
                         defaultValue={asset.scenario}
                         onChange={(e) => setNewName(e.target.value)}
                         onKeyDown={(e) => {
                           if(e.key === 'Enter') {
                             onUpdate(asset.id, { scenario: newName || asset.scenario });
                             setIsEditName(false);
                           }
                         }}
                       />
                       <button onClick={() => setIsEditName(false)} className="text-[var(--text-secondary)]"><X size={18}/></button>
                     </div>
                   ) : (
                     <h2 className="text-xl font-heading font-bold text-[var(--text-primary)] leading-tight flex-1">
                       {asset.scenario || 'Untitled'}
                     </h2>
                   )}
                   {!isEditName && (
                     <button onClick={() => setIsEditName(true)} className="text-[var(--text-tertiary)] hover:text-[var(--accent-primary)] p-1">
                       <Edit2 size={16} />
                     </button>
                   )}
                 </div>
                 
                 <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)]">
                    <span className="uppercase font-bold tracking-wider">{asset.type}</span>
                    <span>•</span>
                    <span>{asset.format || 'Unknown Format'}</span>
                 </div>
              </div>

              {/* Attribution */}
              <div className="flex items-center gap-3 p-3 bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-subtle)]">
                 {creatorInfo.avatar ? (
                    <img src={creatorInfo.avatar} className="w-10 h-10 rounded-full object-cover" alt="" />
                 ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 text-white flex items-center justify-center font-bold text-sm">
                      {creatorInfo.name?.charAt(0) || 'U'}
                    </div>
                 )}
                 <div className="flex-1">
                    <div className="text-sm font-bold text-[var(--text-primary)]">{creatorInfo.name}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{formatDate(asset.createdAt)}</div>
                 </div>
              </div>

              {/* Status & Deployment */}
              <div>
                 <h3 className="text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-3">Status & Deployment</h3>
                 <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                       <button 
                         onClick={() => handleUpdateStatus(AssetStatus.Available)}
                         disabled={isUpdating}
                         className={`px-3 py-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-2 transition-all ${asset.status === AssetStatus.Available ? 'bg-green-50 border-green-200 text-green-700 ring-1 ring-green-500' : 'bg-[var(--bg-primary)] border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'}`}
                       >
                          {isUpdating && asset.status !== AssetStatus.Available ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle size={14} />} Available
                       </button>
                       <button 
                         onClick={() => handleUpdateStatus(AssetStatus.Deployed)}
                         disabled={isUpdating}
                         className={`px-3 py-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-2 transition-all ${asset.status === AssetStatus.Deployed ? 'bg-red-50 border-red-200 text-red-700 ring-1 ring-red-500' : 'bg-[var(--bg-primary)] border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'}`}
                       >
                          {isUpdating && asset.status !== AssetStatus.Deployed ? <Loader2 className="animate-spin" size={14} /> : <Lock size={14} />} Deployed
                       </button>
                    </div>
                 </div>
              </div>

              {/* Organization */}
              <div>
                 <h3 className="text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-3">Organization</h3>
                 <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Campaign (Folder)</label>
                      <select 
                        className="input-field text-sm py-2"
                        value={asset.campaignId || ''}
                        onChange={(e) => onUpdate(asset.id, { campaignId: e.target.value || undefined })}
                      >
                        <option value="">Unassigned</option>
                        {campaignOptions.map(option => (
                          <option key={option.c.id} value={option.c.id}>
                             {/* Add visible hierarchy using dashes or non-breaking spaces */}
                             {'\u00A0\u00A0'.repeat(option.depth)}
                             {option.depth > 0 ? '— ' : ''}
                             {option.c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                       <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Tags</label>
                       <div className="flex flex-wrap gap-1.5">
                          {asset.tags.map(tag => (
                            <span key={tag} className="px-2 py-1 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded text-xs text-[var(--text-secondary)]">
                              #{tag}
                            </span>
                          ))}
                          <button className="px-2 py-1 border border-dashed border-[var(--border-default)] rounded text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:border-[var(--text-primary)]">
                             + Add
                          </button>
                       </div>
                    </div>
                 </div>
              </div>

              {/* Generation Data */}
              {asset.generationPrompt && (
                 <div>
                    <h3 className="text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">Prompt Details</h3>
                    <div className="p-3 bg-[var(--bg-secondary)] rounded-lg text-xs text-[var(--text-secondary)] leading-relaxed italic border border-[var(--border-subtle)]">
                       "{asset.generationPrompt || asset.scenario}"
                    </div>
                 </div>
              )}
           </div>

           {/* Footer Actions */}
           <div className="p-4 border-t border-[var(--border-subtle)] bg-[var(--bg-secondary)] grid grid-cols-2 gap-3">
               <button 
                 onClick={handleDownload}
                 disabled={isDownloading}
                 className="btn-secondary flex items-center justify-center gap-2 text-xs bg-[var(--bg-primary)] border border-[var(--border-default)] hover:bg-[var(--bg-tertiary)] py-2 rounded-lg font-medium transition-colors"
               >
                  {isDownloading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} 
                  {isDownloading ? 'Downloading...' : 'Download'}
               </button>
               <button 
                 type="button"
                 onClick={handleDeleteClick}
                 className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-colors"
               >
                  <Trash2 size={14} />
                  Delete
               </button>
           </div>
        </div>
      </div>
    </div>,
    document.body
  );
};